// @ts-check

/**
 * Siembra de notificationTemplates desde el catálogo del código
 * (COMMUNICATION_TEMPLATES): botón "Sembrar del catálogo" de PlantillasWhatsApp
 * (superadmin).
 *
 * Las reglas 0D dejan crear una plantilla desde el cliente solo así: id del
 * documento == key, active false y source 'catalog_seed'. Nunca se pisa una
 * existente: eso sería un update, y la regla limita el update a active.
 */

/** Key usable como id de documento: minúsculas, dígitos y guion bajo. */
export const keyValida = (key) => typeof key === "string" && /^[a-z0-9_]{1,100}$/.test(key);

/**
 * Datos del doc notificationTemplates/{tpl.key} para una plantilla del
 * catálogo, con el schema que ya usa el sistema (variableSchema con
 * {position, name, description}).
 *
 * @param {Record<string, any>} tpl - entrada de COMMUNICATION_TEMPLATES
 * @param {any} ts - serverTimestamp()
 */
export function payloadSiembra(tpl, ts) {
  const variableSchema = (tpl.variables || []).map((v) => ({
    position: v.idx,
    name: v.name,
    description: v.source ? `Origen: ${v.source}` : "",
  }));
  return {
    key: tpl.key,
    metaTemplateName: tpl.metaTemplateName || tpl.key,
    category: tpl.category || "general",
    stage: tpl.stage ?? null,
    label: tpl.label || tpl.key,
    description: tpl.description || "",
    body: tpl.body || "",
    language: tpl.language || "es",
    metaCategory: tpl.metaCategory || "UTILITY",
    metaStatus: tpl.metaStatus || "PENDING_SUBMIT",
    variableSchema,
    requiresHumanConfirmation: !!tpl.requiresHumanConfirmation,
    active: false,
    createdAt: ts,
    updatedAt: ts,
    source: "catalog_seed",
  };
}

/**
 * Siembra las plantillas que faltan. Solo crea: lo que ya está en la lista
 * cargada se saltea sin leer, y antes de cada alta se confirma con un get
 * que el doc no exista (pudo crearse después de cargar la lista).
 *
 * @param {Array<Record<string, any>>} catalogo
 * @param {{
 *   existentes: Set<string>,
 *   existe: (key: string) => Promise<boolean>,
 *   crear: (key: string, datos: Record<string, any>) => Promise<any>,
 *   ts: any,
 * }} deps
 * @returns {Promise<{ creadas: string[], salteadas: string[], fallidas: string[] }>}
 */
export async function sembrarPlantillas(catalogo, { existentes, existe, crear, ts }) {
  const out = { creadas: [], salteadas: [], fallidas: [] };
  for (const tpl of catalogo || []) {
    const key = tpl && tpl.key;
    if (!keyValida(key)) {
      out.fallidas.push(String(key));
      continue;
    }
    if (existentes.has(key)) {
      out.salteadas.push(key);
      continue;
    }
    try {
      if (await existe(key)) {
        out.salteadas.push(key);
        continue;
      }
      await crear(key, payloadSiembra(tpl, ts));
      out.creadas.push(key);
    } catch (err) {
      console.error(`Sembrar plantilla ${key}:`, err);
      out.fallidas.push(key);
    }
  }
  return out;
}
